/**
 * InventoryPendingSummaryBanner — FE-248 v2.
 *
 * Sticky summary that appears above the inventory list when many items
 * have pending requests at once (overflow case in Wireframe 3). The
 * inline strips auto-collapse when this banner is active to keep the
 * list scannable; the banner becomes the primary triage entry point.
 *
 * Visibility rule: render when `itemsWithPending > 5`. Below the
 * threshold the inline strips are enough.
 *
 * Tap [Review all →] routes to the existing BorrowRequestsManager
 * filtered to this group — that screen is built for triage and
 * surfaces decline-with-notes, waitlist reordering, etc.
 */

import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

import { fpColors } from '../../../styles/theme';

export interface InventoryPendingSummaryBannerProps {
  groupId: number;
  itemsWithPending: number;
  totalRequests: number;
  expiringSoonCount: number;
}

export const InventoryPendingSummaryBanner: React.FC<
  InventoryPendingSummaryBannerProps
> = ({ groupId, itemsWithPending, totalRequests, expiringSoonCount }) => {
  const navigation = useNavigation<any>();

  return (
    <View
      style={styles.banner}
      testID={`pending-summary-banner-${groupId}`}
    >
      <View style={styles.iconWrap}>
        <MaterialIcons
          name="warning"
          size={20}
          color={
            (fpColors as any).onTertiaryContainer ?? fpColors.primary
          }
        />
      </View>
      <View style={styles.body}>
        <Text style={styles.titleLine}>
          {itemsWithPending} of your items have pending requests
        </Text>
        <Text style={styles.subtitleLine}>
          Total: {totalRequests} request{totalRequests === 1 ? '' : 's'}
          {expiringSoonCount > 0 ? (
            <Text style={styles.urgentText}>
              {' · '}
              {expiringSoonCount} expire{expiringSoonCount === 1 ? 's' : ''} today
            </Text>
          ) : null}
        </Text>
      </View>
      <Pressable
        onPress={() =>
          navigation.navigate('BorrowRequests', {
            filter: 'pending',
            groupId,
          } as any)
        }
        accessibilityRole="button"
        accessibilityLabel="Review all pending requests"
        style={({ pressed }) => [
          styles.cta,
          pressed && { opacity: 0.85 },
        ]}
        hitSlop={6}
      >
        <Text style={styles.ctaText}>Review all</Text>
        <MaterialIcons
          name="chevron-right"
          size={18}
          color={
            (fpColors as any).onPrimaryContainer ?? fpColors.primary
          }
        />
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor:
      (fpColors as any).tertiaryContainer ??
      (fpColors as any).primaryContainer ??
      '#FFE0B2',
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginHorizontal: 12,
    marginBottom: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor:
      (fpColors as any).outlineVariant ?? 'rgba(0,0,0,0.08)',
  },
  iconWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.5)',
  },
  body: {
    flex: 1,
  },
  titleLine: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color:
      (fpColors as any).onTertiaryContainer ?? fpColors.onSurface,
  },
  subtitleLine: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color:
      (fpColors as any).onTertiaryContainer ?? fpColors.onSurfaceVariant,
    marginTop: 1,
  },
  urgentText: {
    fontFamily: 'Inter_700Bold',
    color: (fpColors as any).error ?? '#B3261E',
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.6)',
  },
  ctaText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color:
      (fpColors as any).onPrimaryContainer ?? fpColors.primary,
    letterSpacing: 0.2,
  },
});
